import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { PartnerMembershipService } from '@sisoma/auth';
import { EmptyState, EmptyStateBody, LinkButton } from '@sisoma/ui';

/**
 * Shown to someone whose seat in this partner area has been suspended.
 *
 * Kept separate from the no-role screen even though both arrive as an empty
 * capability set, because they mean opposite things. "No role yet" is a gap
 * someone is expected to fill; a suspended seat is a decision somebody already
 * made. Telling a suspended member to go and ask for a role would send them to
 * their owner with the wrong question.
 *
 * No action button: there is nothing useful for them to press. The owner's
 * email is offered when we know it, and the partner switcher stays in the
 * sidenav footer so they can leave for an area they are still active in.
 */
@Component({
  selector: 'app-suspended',
  imports: [EmptyState, EmptyStateBody, LinkButton],
  templateUrl: './suspended.html',
  styleUrl: './suspended.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Suspended {
  private readonly membership = inject(PartnerMembershipService);

  /**
   * The area's name, or a generic stand-in.
   *
   * Off `GET /v1/me/capabilities` rather than the switcher list, because the
   * list cannot answer for the very person this screen is for: it returns only
   * the areas the caller holds an *active* seat in, so a suspended member's
   * area is absent from it. The fallback now covers a `null` from the API, not
   * a list that structurally never had this area in it.
   */
  protected readonly partnerName = computed(
    () => this.membership.partnerName() ?? 'this partner area',
  );

  /** Who to ask. Absent when the area has no resolvable owner. */
  protected readonly ownerEmail = this.membership.ownerEmail;
}
